import React, { useState, useEffect } from 'react';
import { Check, Copy, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import PromptUsageMeter from './PromptUsageMeter';
import BillingToggle from '../pricing/BillingToggle';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { getSubscriptionTiers } from "@/services/subscriptionService";

// Feature flag for upgrade functionality - Set to true to enable payments
const ENABLE_UPGRADES = true; // Set to true to activate subscription system

interface PromptUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  usageInfo?: {
    canCopy: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null;
  onUpgrade?: (tierId: string, billingPeriod: 'monthly' | 'yearly') => void; // Updated interface
}

interface EnhancedTier {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  isPopular?: boolean;
}

const PromptUpgradeDialog: React.FC<PromptUpgradeDialogProps> = ({ 
  open, 
  onOpenChange,
  usageInfo,
  onUpgrade
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [isInterested, setIsInterested] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [tiers, setTiers] = useState<EnhancedTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Load subscription tiers from database
  useEffect(() => {
    const loadTiers = async () => {
      try {
        const subscriptionTiers = await getSubscriptionTiers();
        const enhancedTiers = subscriptionTiers.map(tier => ({
          id: tier.id,
          name: tier.name,
          description: tier.description,
          monthlyPrice: tier.name.toLowerCase() === 'free' ? 0 :
                       tier.name.toLowerCase() === 'plus' ? 10 : 
                       tier.name.toLowerCase() === 'pro' ? 20 : tier.price,
          yearlyPrice: tier.name.toLowerCase() === 'free' ? 0 :
                      tier.name.toLowerCase() === 'plus' ? 100 : 
                      tier.name.toLowerCase() === 'pro' ? 200 : tier.price * 10,
          features: tier.features || [],
          isPopular: tier.name.toLowerCase() === 'plus'
        })).sort((a, b) => a.monthlyPrice - b.monthlyPrice);
        
        setTiers(enhancedTiers);
      } catch (error) {
        console.error('Error loading tiers:', error);
        // Fallback to hardcoded tiers if database fails
        setTiers([
          {
            id: 'free',
            name: 'Free',
            description: 'Basic access to prompts',
            monthlyPrice: 0,
            yearlyPrice: 0,
            features: [
              '5 prompt copies per month',
              'Browse unlimited prompts',
              '5 Product Copilot credits/month',
              'Basic support',
              'Community access'
            ]
          },
          {
            id: 'plus',
            name: 'Plus',
            description: 'For regular users',
            monthlyPrice: 10,
            yearlyPrice: 100,
            features: [
              'Unlimited prompt copies',
              'Unlimited prompt saves',
              'Access to public prompts',
              'Create your own prompts',
              '100 Product Copilot credits/month'
            ],
            isPopular: true
          },
          {
            id: 'pro',
            name: 'Pro',
            description: 'Enhanced access with more features',
            monthlyPrice: 20,
            yearlyPrice: 200,
            features: [
              'Everything in Plus',
              '300 Product Copilot credits/month',
              'Product Copilot access',
              'Custom prompt collections',
              'Early access to new features'
            ]
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (open && ENABLE_UPGRADES) {
      loadTiers();
    }
  }, [open]);
  
  const handleSubmitInterest = async () => {
    if (!user) {
      toast.error("You need to be signed in to express interest");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save interest to database
      const { error } = await supabase
        .from('premium_interest')
        .insert({
          user_id: user.id,
          email: email || user.email
        });
      
      if (error) throw error;
      
      toast.success("Thanks for your interest! We'll notify you when premium features are available.");
      setIsInterested(true);
    } catch (err) {
      console.error("Error recording premium interest:", err);
      toast.error("Failed to record your interest. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpgradeNow = async (tierId: string, actionType?: 'upgrade' | 'downgrade' | 'cancel') => {
    if (onUpgrade) {
      // Pass the current billing period state to the parent
      onUpgrade(tierId, billingPeriod);
      return;
    }

    if (!user) {
      navigate('/sign-in', { state: { returnUrl: '/pricing' } });
      return;
    }
    
    if (isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      // Use the current billingPeriod state
      console.log('PromptUpgradeDialog: Creating checkout with correct billing period', { 
        tierId, 
        billingPeriod, // This should now be the correct value
        actionType
      });
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          tierId, 
          billingPeriod, // Use the state variable directly
          isUpgrade: !!actionType
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        console.log('PromptUpgradeDialog: Opening checkout URL', data.url);
        window.open(data.url, '_blank');
        onOpenChange(false);
      } else if (data?.success) {
        toast.success(data.message || 'Subscription updated successfully!');
        onOpenChange(false);
      } else {
        throw new Error('No checkout URL or success confirmation returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout process. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // If upgrades are disabled, show a modified dialog for alpha testers
  if (!ENABLE_UPGRADES) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alpha Testing Mode</DialogTitle>
            <DialogDescription>
              We are in alpha mode and when we move to beta, you will be able to access premium options. If you wish to get early access to the premium features, please select 'Yes' below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {!isInterested ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Would you be interested in premium features when we launch? Let us know so we can keep you updated!
                </p>
                <Input 
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => onOpenChange(false)}
                    className="flex-1"
                  >
                    No thanks
                  </Button>
                  <Button 
                    variant="default"
                    onClick={handleSubmitInterest}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Yes, I'm interested
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <Check className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Thanks for your interest!</p>
                <Button 
                  variant="default"
                  onClick={() => onOpenChange(false)}
                  className="mt-4"
                >
                  Continue Exploring
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Regular upgrade dialog for when feature is enabled
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Upgrade Your Account</DialogTitle>
          <DialogDescription>
            You've reached your monthly limit. Choose a plan to continue using AI features.
          </DialogDescription>
        </DialogHeader>
        
        {usageInfo && (
          <div className="mb-4">
            <PromptUsageMeter 
              usedCount={usageInfo.usedCount}
              limit={usageInfo.limit}
              percentUsed={usageInfo.percentUsed}
            />
          </div>
        )}

        <BillingToggle 
          billingPeriod={billingPeriod}
          onToggle={setBillingPeriod}
        />
        
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 pt-6">
            {tiers.map((tier) => {
              // CRITICAL FIX: Use correct pricing based on current billing period state
              const price = billingPeriod === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;
              const monthlyEquivalent = billingPeriod === 'yearly' && tier.yearlyPrice > 0 ? tier.yearlyPrice / 12 : tier.monthlyPrice;
              const savings = billingPeriod === 'yearly' && tier.yearlyPrice > 0 && tier.monthlyPrice > 0 ? 
                (tier.monthlyPrice * 12) - tier.yearlyPrice : 0;
              const savingsPercentage = billingPeriod === 'yearly' && tier.yearlyPrice > 0 && tier.monthlyPrice > 0 ? 
                Math.round((savings / (tier.monthlyPrice * 12)) * 100) : 0;

              console.log(`PromptUpgradeDialog - Tier ${tier.name} pricing calculation:`, { 
                currentBillingPeriod: billingPeriod,
                monthlyPrice: tier.monthlyPrice, 
                yearlyPrice: tier.yearlyPrice, 
                displayPrice: price,
                monthlyEquivalent,
                savings,
                savingsPercentage
              });

              return (
                <div 
                  key={tier.id}
                  className={`relative border rounded-lg p-4 h-full min-h-[420px] ${
                    tier.isPopular 
                      ? 'border-primary ring-2 ring-primary/20' 
                      : 'border-border'
                  }`}
                >
                  {tier.isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <Badge className="bg-primary text-primary-foreground px-3 py-1 text-sm font-bold shadow-lg">
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  
                  <div className="text-center mb-4 pt-4">
                    <h3 className="text-lg font-semibold mb-1">{tier.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                    
                    <div className="space-y-1">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-2xl font-bold">
                          {price === 0 ? 'Free' : `$${price.toFixed(price < 1 ? 2 : 0)}`}
                        </span>
                        {price > 0 && (
                          <span className="text-sm text-muted-foreground">
                            /{billingPeriod === 'yearly' ? 'year' : 'month'}
                          </span>
                        )}
                      </div>
                      
                      {billingPeriod === 'yearly' && tier.yearlyPrice > 0 && tier.monthlyPrice > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">
                            ${monthlyEquivalent.toFixed(2)}/month billed annually
                          </p>
                          {savings > 0 && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 text-xs">
                              Save ${savings} ({savingsPercentage}% off)
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full mb-4"
                    variant={tier.isPopular ? 'default' : 'outline'}
                    onClick={() => handleUpgradeNow(tier.name.toLowerCase(), tier.name.toLowerCase() === 'free' ? 'cancel' : 'upgrade')}
                    disabled={tier.name.toLowerCase() === 'free' || isProcessing}
                  >
                    {tier.name.toLowerCase() === 'free' ? 'Current Plan' : 
                     isProcessing ? 'Processing...' : 'Upgrade Now'}
                  </Button>
                  
                  <div className="space-y-2">
                    {tier.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Check className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-xs">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Maybe Later
          </Button>
          <Button 
            variant="default"
            onClick={() => {
              onOpenChange(false);
              navigate('/pricing');
            }}
          >
            View Full Plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptUpgradeDialog;
